import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useCallback, useEffect } from "react";
import {
  ArrowLeft,
  ExternalLink,
  Copy,
  Check,
  Loader2,
  Globe,
  Server,
  HardDrive,
  MapPin,
  Cpu,
  Moon,
  AlertTriangle,
  Rocket,
  ChevronDown,
  Menu,
  RotateCw,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { normalizeArrayResponse } from "@/lib/repository-route-response-normalizers";
import {
  canEnableRepositoryDeploy,
  canShowRepositoryDeployUi,
} from "@/lib/repository-deploy-ui";
import { cn, relativeTime } from "@/lib/utils";
import { useMobileLayout } from "@/hooks/use-mobile-layout";
import { useSessionUI } from "@/stores/session-ui";
import { fetchSessionList } from "@/lib/session-list-query";
import {
  SettingsCard,
  DetailRow,
} from "@/components/settings/settings-primitives";

export const Route = createFileRoute("/_authed/$repoId/deploy")({
  component: DeployPage,
  validateSearch: (search: Record<string, unknown>) => ({
    /**
     * The session ID passed from the top bar when the user clicks Deploy/Redeploy.
     * This tells the deploy page which session to use for deploy operations
     * without requiring the user to pick from a dropdown.
     */
    sessionId: typeof search.sessionId === "string" ? search.sessionId : undefined,
  }),
});

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const API_BASE = import.meta.env.VITE_API_URL || "";

/**
 * Polling interval for deployment status during active operations.
 * 2.5 seconds balances responsiveness with API load. Build operations
 * typically take 30-120s, so this gives ~12-48 polls per deploy.
 */
const DEPLOY_STATUS_POLL_INTERVAL_MS = 2_500;

/**
 * Stale time for the repos query on this page. We reuse the shared ["repos"]
 * cache from the sidebar/session pages, so a moderate stale time avoids
 * unnecessary refetches while keeping data reasonably fresh.
 */
const REPOS_QUERY_STALE_TIME_MS = 10_000;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type DeploymentStatus =
  | "not_deployed"
  | "building"
  | "pushing"
  | "deploying"
  | "live"
  | "failed"
  | "destroyed";

interface DeploymentDto {
  id: string;
  repositoryId: string;
  status: DeploymentStatus;
  url: string | null;
  customUrl: string | null;
  subdomain: string;
  flyAppName: string | null;
  flyMachineId: string | null;
  flyRegion: string;
  lastDeployedAt: string | null;
  lastDeployError: string | null;
  lastDeployImageRef: string | null;
  deployCount: number;
}

interface DeploymentResponse {
  deployEnabled: boolean;
  deployment: DeploymentDto | null;
}

interface DeployEnvResponse {
  content: string;
  hasExistingContent?: boolean;
  keys?: string[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function apiFetch<T = unknown>(
  path: string,
  opts?: RequestInit,
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    ...opts,
    headers: { "Content-Type": "application/json", ...opts?.headers },
  });
  if (!res.ok) {
    const body = await res.text();
    let parsedErrorMessage: string | null = null;
    try {
      const parsedBody = JSON.parse(body) as { error?: unknown };
      if (typeof parsedBody.error === "string" && parsedBody.error.trim()) {
        parsedErrorMessage = parsedBody.error;
      }
    } catch {
      /* non-JSON error */
    }
    throw new Error(
      parsedErrorMessage || body || `Request failed (${res.status})`,
    );
  }
  return res.json() as Promise<T>;
}

function isActiveDeployStatus(status: DeploymentStatus): boolean {
  return (
    status === "building" || status === "pushing" || status === "deploying"
  );
}

/** Human-readable region names for the most common Fly.io regions. */
const FLY_REGION_LABELS: Record<string, string> = {
  iad: "Ashburn, Virginia (US East)",
  ord: "Chicago, Illinois (US Central)",
  sjc: "San Jose, California (US West)",
  lax: "Los Angeles, California",
  sea: "Seattle, Washington",
  ewr: "Secaucus, New Jersey",
  atl: "Atlanta, Georgia",
  dfw: "Dallas, Texas",
  den: "Denver, Colorado",
  yyz: "Toronto, Canada",
  ams: "Amsterdam, Netherlands",
  cdg: "Paris, France",
  fra: "Frankfurt, Germany",
  lhr: "London, United Kingdom",
  mad: "Madrid, Spain",
  waw: "Warsaw, Poland",
  nrt: "Tokyo, Japan",
  hkg: "Hong Kong",
  sin: "Singapore",
  syd: "Sydney, Australia",
  bom: "Mumbai, India",
  gru: "São Paulo, Brazil",
  scl: "Santiago, Chile",
  jnb: "Johannesburg, South Africa",
};

function getRegionLabel(region: string): string {
  return FLY_REGION_LABELS[region] ?? region.toUpperCase();
}

// ---------------------------------------------------------------------------
// Progress stepper config
// ---------------------------------------------------------------------------

interface DeployStep {
  key: DeploymentStatus;
  label: string;
  description: string;
}

/**
 * The ordered steps in a deployment pipeline. Used to render the progress
 * stepper during active deployments. The order matters — it represents
 * the linear progression from build to live.
 */
const DEPLOY_STEPS: DeployStep[] = [
  { key: "building", label: "Build", description: "Building Docker image in sandbox" },
  { key: "pushing", label: "Push", description: "Pushing image to Fly registry" },
  { key: "deploying", label: "Deploy", description: "Starting Fly.io machine" },
  { key: "live", label: "Live", description: "Your app is running" },
];

function getStepIndex(status: DeploymentStatus): number {
  return DEPLOY_STEPS.findIndex((s) => s.key === status);
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function DeployPage() {
  const { repoId } = Route.useParams();
  const { sessionId: passedSessionId } = Route.useSearch();
  const queryClient = useQueryClient();
  const isMobileLayout = useMobileLayout();
  const toggleLeftSidebar = useSessionUI((s) => s.toggleLeftSidebar);

  /**
   * Track which session to use for deploys. We no longer blindly trust the
   * URL param — it is validated against the loaded session list to avoid
   * deploying from a stale/deleted/wrong-repo session.
   */
  const [manuallySelectedSessionId, setManuallySelectedSessionId] = useState<string | undefined>(undefined);

  // ── Repo data ───────────────────────────────────────────────────────
  const { data: repos } = useQuery({
    queryKey: ["repos"],
    staleTime: REPOS_QUERY_STALE_TIME_MS,
    queryFn: async () => {
      const res = await api.api.repos.$get();
      if (!res.ok) return [];
      return normalizeArrayResponse<{
        id: string;
        name: string;
        deployEnabled?: boolean;
        isCordNewProject?: boolean;
      }>({ value: await res.json() });
    },
  });
  const repo = (repos ?? []).find((r) => r.id === repoId);
  const deployEnabled = repo?.deployEnabled === true;
  const canEnableDeploy = canEnableRepositoryDeploy({ repository: repo });
  const showDeployUi = canShowRepositoryDeployUi({ repository: repo });

  // ── Sessions (for deploy action) ───────────────────────────────────
  const { data: sessions } = useQuery({
    queryKey: ["sessions-for-deploy", repoId],
    queryFn: async () => {
      const all = await fetchSessionList<{
        id: string;
        title: string | null;
        status: string;
        repositoryId?: string;
        archivedAt?: string | null;
      }>({ repositoryId: repoId });
      return all.filter(
        (s) =>
          s.repositoryId === repoId &&
          !s.archivedAt &&
          s.status !== "error",
      );
    },
  });
  const activeSessions = sessions ?? [];

  // Resolve the effective session to deploy from.
  // Priority: manual selection > validated URL param > first active session.
  // The URL param is only used if it actually matches a known active session,
  // preventing deploys from stale/deleted/wrong-repo session IDs.
  const validatedPassedSessionId =
    passedSessionId && activeSessions.some((s) => s.id === passedSessionId)
      ? passedSessionId
      : undefined;
  const effectiveSessionId =
    manuallySelectedSessionId ?? validatedPassedSessionId ?? activeSessions[0]?.id;
  const effectiveSession = activeSessions.find((s) => s.id === effectiveSessionId);

  /**
   * When the URL contains a sessionId that doesn't match any active session,
   * show a warning so the user knows why deploy may be disabled or targeting
   * a different session than expected.
   */
  const hasInvalidPassedSession =
    !!passedSessionId && !validatedPassedSessionId && activeSessions.length > 0;

  // ── Deployment status ───────────────────────────────────────────────
  const deploymentQuery = useQuery({
    queryKey: ["deployment", repoId],
    enabled: showDeployUi,
    queryFn: () =>
      apiFetch<DeploymentResponse>(`/api/repos/${repoId}/deployment`),
    refetchInterval: (query) => {
      const status = query.state.data?.deployment?.status;
      if (status && isActiveDeployStatus(status as DeploymentStatus)) {
        return DEPLOY_STATUS_POLL_INTERVAL_MS;
      }
      return false;
    },
  });

  const deployment = deploymentQuery.data?.deployment ?? null;
  const status: DeploymentStatus =
    (deployment?.status as DeploymentStatus) ?? "not_deployed";
  const busy = isActiveDeployStatus(status);

  // ── Deploy / Destroy mutations ──────────────────────────────────────
  const deployMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      return apiFetch<{ deployment?: DeploymentDto }>(
        `/api/repos/${repoId}/deploy`,
        {
          method: "POST",
          body: JSON.stringify({ sessionId }),
        },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deployment", repoId] });
      toast.success("Deployment started");
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Failed to start deploy",
      );
    },
  });

  const destroyMutation = useMutation({
    mutationFn: async () => {
      return apiFetch(`/api/repos/${repoId}/deployment`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deployment", repoId] });
      toast.success("Deployment destroyed");
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Failed to destroy deployment",
      );
    },
  });

  const toggleDeployMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      return apiFetch<DeploymentResponse>(
        `/api/repos/${repoId}/deploy-settings`,
        {
          method: "PATCH",
          body: JSON.stringify({ deployEnabled: enabled }),
        },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deployment", repoId] });
      queryClient.invalidateQueries({ queryKey: ["repos"] });
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to update deploy settings",
      );
    },
  });

  const handleDeploy = useCallback(() => {
    if (!effectiveSessionId) {
      toast.error("No active session to deploy from");
      return;
    }
    deployMutation.mutate(effectiveSessionId);
  }, [effectiveSessionId, deployMutation]);

  // ── Loading / not-deploy-enabled states ─────────────────────────────
  if (!repo) {
    return (
      <PageShell
        repoId={repoId}
        isMobileLayout={isMobileLayout}
        toggleLeftSidebar={toggleLeftSidebar}
      >
        <div className="flex items-center justify-center py-20 text-sm text-zinc-500">
          <Loader2 className="mr-2 size-4 animate-spin" />
          Loading…
        </div>
      </PageShell>
    );
  }

  if (!canEnableDeploy) {
    return (
      <PageShell
        repoId={repoId}
        isMobileLayout={isMobileLayout}
        toggleLeftSidebar={toggleLeftSidebar}
      >
        <DeployUnavailableCard repoId={repoId} />
      </PageShell>
    );
  }

  if (!deployEnabled) {
    return (
      <PageShell
        repoId={repoId}
        isMobileLayout={isMobileLayout}
        toggleLeftSidebar={toggleLeftSidebar}
      >
        <EnableDeployCard
          enabling={toggleDeployMutation.isPending}
          onEnable={() => {
            toggleDeployMutation.mutate(true);
            toast.success("Deploy enabled — new threads will be guided toward a deployable architecture");
          }}
        />
      </PageShell>
    );
  }

  return (
    <PageShell
      repoId={repoId}
      isMobileLayout={isMobileLayout}
      toggleLeftSidebar={toggleLeftSidebar}
      headerRight={
        <DeployPageActionButton
          status={status}
          busy={busy || deployMutation.isPending}
          hasSession={!!effectiveSessionId}
          onDeploy={handleDeploy}
        />
      }
    >
      {/* Warning when URL sessionId is stale/invalid */}
      {hasInvalidPassedSession && (
        <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-4 py-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-400" />
            <div className="text-xs text-amber-200/90">
              <p className="font-medium">Session not found</p>
              <p className="mt-0.5 text-amber-200/70">
                The linked session is no longer available.
                {activeSessions.length > 0
                  ? ` Deploying from "${activeSessions[0]?.title ?? "most recent session"}" instead.`
                  : " Start a thread first to deploy."}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Session selector when multiple sessions and no specific one targeted */}
      {!passedSessionId && activeSessions.length > 1 && !busy && !deployMutation.isPending && (
        <div className="rounded-lg border border-white/[0.06] bg-zinc-900/50 px-4 py-3">
          <label className="block text-xs font-medium text-zinc-400 mb-2">
            Deploy from thread
          </label>
          <select
            value={effectiveSessionId ?? ""}
            onChange={(e) => setManuallySelectedSessionId(e.target.value || undefined)}
            className="w-full rounded-md border border-white/[0.08] bg-zinc-800 px-3 py-1.5 text-sm text-zinc-200 outline-none focus:border-zinc-600"
          >
            {activeSessions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title ?? `Session ${s.id.slice(0, 8)}`}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Progress stepper shown during active deploys */}
      {(busy || deployMutation.isPending) && (
        <DeployProgressStepper
          status={status}
          sessionName={effectiveSession?.title ?? undefined}
        />
      )}

      {/* Status banner (shown when NOT actively deploying) */}
      {!busy && !deployMutation.isPending && (
        <DeploymentStatusBanner
          deployment={deployment}
          status={status}
        />
      )}

      {/* Environment Variables */}
      {deployment && (
        <DeployEnvVarsSection deploymentId={deployment.id} />
      )}

      {/* Deployment Configuration */}
      {deployment && (
        <DeploymentConfigSection deployment={deployment} />
      )}

      {/* Danger Zone */}
      <DangerZoneSection
        deployment={deployment}
        status={status}
        onDestroy={() => destroyMutation.mutate()}
        destroying={destroyMutation.isPending}
        onDisableDeploy={() => {
          toggleDeployMutation.mutate(false);
          toast.success("Deploy disabled");
        }}
        disabling={toggleDeployMutation.isPending}
      />
    </PageShell>
  );
}

// ---------------------------------------------------------------------------
// Page shell (consistent layout across all states)
// ---------------------------------------------------------------------------

function PageShell(args: {
  repoId: string;
  isMobileLayout: boolean;
  toggleLeftSidebar: () => void;
  headerRight?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div
      className="h-full overflow-y-auto"
      style={
        args.isMobileLayout
          ? { paddingTop: "var(--safe-area-top, 0px)" }
          : undefined
      }
    >
      <div
        className={cn(
          "mx-auto max-w-2xl animate-[fade-in_0.2s_ease-out]",
          args.isMobileLayout ? "px-4 py-4" : "px-8 py-10",
        )}
      >
        {/* Header */}
        <div className="mb-8">
          <div className="mb-4 flex items-center gap-2">
            {args.isMobileLayout && (
              <button
                type="button"
                onClick={args.toggleLeftSidebar}
                className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-white/[0.06] text-zinc-400 transition-colors hover:text-zinc-200"
                aria-label="Open menu"
              >
                <Menu className="size-4" />
              </button>
            )}
            <Link
              to="/$repoId"
              params={{ repoId: args.repoId }}
              className="inline-flex items-center gap-1.5 text-xs text-zinc-500 transition-colors hover:text-zinc-300"
            >
              <ArrowLeft className="size-3" />
              Back to project
            </Link>
          </div>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-xl border border-white/[0.06] bg-zinc-900">
                <Rocket className="size-5 text-zinc-400" />
              </div>
              <div>
                <h1 className="text-lg font-semibold tracking-tight text-zinc-100">
                  Deploy
                </h1>
                <p className="text-xs text-zinc-500">
                  Manage your production deployment
                </p>
              </div>
            </div>
            {args.headerRight}
          </div>
        </div>

        {/* Content */}
        <div className="space-y-5">{args.children}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Deploy page action button (simpler - uses the passed session)
// ---------------------------------------------------------------------------

function DeployPageActionButton(args: {
  status: DeploymentStatus;
  busy: boolean;
  hasSession: boolean;
  onDeploy: () => void;
}) {
  const label = args.busy
    ? "Deploying…"
    : args.status === "live"
      ? "Redeploy"
      : args.status === "failed"
        ? "Retry deploy"
        : "Deploy";

  return (
    <button
      type="button"
      disabled={args.busy || !args.hasSession}
      onClick={args.onDeploy}
      className={cn(
        "inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50",
        args.status === "failed"
          ? "bg-red-500/15 text-red-300 hover:bg-red-500/25"
          : "bg-zinc-100 text-zinc-900 hover:bg-white",
      )}
      title={
        !args.hasSession
          ? "Start a thread first to deploy from its sandbox"
          : undefined
      }
    >
      {args.busy ? (
        <Loader2 className="size-3.5 animate-spin" />
      ) : args.status === "live" ? (
        <RotateCw className="size-3.5" />
      ) : (
        <Rocket className="size-3.5" />
      )}
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Progress stepper — shown during active deployment
// ---------------------------------------------------------------------------

function DeployProgressStepper(args: {
  status: DeploymentStatus;
  sessionName?: string;
}) {
  const currentStepIndex = getStepIndex(args.status);
  const [elapsed, setElapsed] = useState(0);

  // useEffect is necessary here for timer cleanup on unmount (avoid memory leaks).
  // The component only renders during active deploy states, so mounting = start, unmounting = stop.
  useEffect(() => {
    const id = setInterval(() => {
      setElapsed((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const formatElapsed = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  return (
    <SettingsCard>
      <div className="space-y-5">
        {/* Header with elapsed time */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex size-8 items-center justify-center rounded-lg border border-blue-500/20 bg-blue-500/10">
              <Loader2 className="size-4 animate-spin text-blue-400" />
            </div>
            <div>
              <div className="text-sm font-semibold text-blue-300">
                Deploying…
              </div>
              <div className="text-xs text-zinc-500">
                {DEPLOY_STEPS[currentStepIndex]?.description ?? "Starting…"}
              </div>
            </div>
          </div>
          <div className="text-right text-xs text-zinc-500">
            <span>Elapsed: {formatElapsed(elapsed)}</span>
          </div>
        </div>

        {/* Step indicators */}
        <div className="flex items-center gap-1">
          {DEPLOY_STEPS.map((step, index) => {
            const isCompleted = index < currentStepIndex;
            const isCurrent = index === currentStepIndex;
            const isPending = index > currentStepIndex;

            return (
              <div key={step.key} className="flex flex-1 flex-col items-center gap-2">
                {/* Step dot/indicator */}
                <div className="flex w-full items-center">
                  {/* Progress bar before */}
                  {index > 0 && (
                    <div
                      className={cn(
                        "h-0.5 flex-1 rounded-full transition-colors duration-500",
                        isCompleted || isCurrent
                          ? "bg-blue-400/60"
                          : "bg-zinc-800",
                      )}
                    />
                  )}
                  {/* Dot */}
                  <div
                    className={cn(
                      "relative flex size-6 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-500",
                      isCompleted
                        ? "border-blue-400 bg-blue-400"
                        : isCurrent
                          ? "border-blue-400 bg-blue-400/20"
                          : "border-zinc-700 bg-zinc-900",
                    )}
                  >
                    {isCompleted ? (
                      <Check className="size-3 text-white" />
                    ) : isCurrent ? (
                      <div className="size-2 animate-pulse rounded-full bg-blue-400" />
                    ) : (
                      <div className="size-1.5 rounded-full bg-zinc-600" />
                    )}
                  </div>
                  {/* Progress bar after */}
                  {index < DEPLOY_STEPS.length - 1 && (
                    <div
                      className={cn(
                        "h-0.5 flex-1 rounded-full transition-colors duration-500",
                        isCompleted ? "bg-blue-400/60" : "bg-zinc-800",
                      )}
                    />
                  )}
                </div>
                {/* Label */}
                <span
                  className={cn(
                    "text-xs font-medium transition-colors",
                    isCompleted
                      ? "text-blue-300/70"
                      : isCurrent
                        ? "text-blue-300"
                        : "text-zinc-600",
                  )}
                >
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>

        {/* Session context */}
        {args.sessionName && (
          <div className="text-xs text-zinc-600">
            Deploying from: <span className="text-zinc-400">{args.sessionName}</span>
          </div>
        )}
      </div>
    </SettingsCard>
  );
}

// ---------------------------------------------------------------------------
// Status banner (shown when NOT deploying)
// ---------------------------------------------------------------------------

/** Status configuration for visual rendering. */
function getStatusConfig(status: DeploymentStatus): {
  label: string;
  dotClass: string;
  bgClass: string;
  textClass: string;
  description: string;
} {
  switch (status) {
    case "live":
      return {
        label: "Live",
        dotClass: "bg-emerald-400",
        bgClass: "bg-emerald-500/10 border-emerald-500/20",
        textClass: "text-emerald-300",
        description: "Your app is deployed and accessible",
      };
    case "building":
      return {
        label: "Building",
        dotClass: "bg-blue-400 animate-pulse",
        bgClass: "bg-blue-500/10 border-blue-500/20",
        textClass: "text-blue-300",
        description: "Building Docker image in sandbox…",
      };
    case "pushing":
      return {
        label: "Pushing",
        dotClass: "bg-blue-400 animate-pulse",
        bgClass: "bg-blue-500/10 border-blue-500/20",
        textClass: "text-blue-300",
        description: "Pushing image to registry…",
      };
    case "deploying":
      return {
        label: "Deploying",
        dotClass: "bg-blue-400 animate-pulse",
        bgClass: "bg-blue-500/10 border-blue-500/20",
        textClass: "text-blue-300",
        description: "Starting Fly.io machine…",
      };
    case "failed":
      return {
        label: "Failed",
        dotClass: "bg-red-400",
        bgClass: "bg-red-500/10 border-red-500/20",
        textClass: "text-red-300",
        description: "Last deployment failed",
      };
    case "destroyed":
      return {
        label: "Destroyed",
        dotClass: "bg-zinc-500",
        bgClass: "bg-zinc-500/10 border-zinc-500/20",
        textClass: "text-zinc-400",
        description: "Deployment has been destroyed",
      };
    case "not_deployed":
    default:
      return {
        label: "Not deployed",
        dotClass: "bg-zinc-500",
        bgClass: "bg-zinc-500/10 border-zinc-500/20",
        textClass: "text-zinc-400",
        description: "Deploy your app to get a live URL",
      };
  }
}

function DeploymentStatusBanner(args: {
  deployment: DeploymentDto | null;
  status: DeploymentStatus;
}) {
  const [copied, setCopied] = useState(false);
  const [errorExpanded, setErrorExpanded] = useState(false);
  const url = args.deployment?.url ?? null;
  const statusConfig = getStatusConfig(args.status);

  const handleCopy = useCallback(() => {
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      toast.success("URL copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    });
  }, [url]);

  return (
    <SettingsCard>
      <div className="space-y-4">
        {/* Status row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                "flex size-8 items-center justify-center rounded-lg border",
                statusConfig.bgClass,
              )}
            >
              <span
                className={cn("size-2 rounded-full", statusConfig.dotClass)}
              />
            </div>
            <div>
              <div
                className={cn(
                  "text-sm font-semibold",
                  statusConfig.textClass,
                )}
              >
                {statusConfig.label}
              </div>
              <div className="text-xs text-zinc-500">
                {statusConfig.description}
              </div>
            </div>
          </div>

          {/* Deploy metadata */}
          {args.deployment && args.deployment.deployCount > 0 && (
            <div className="text-right text-xs text-zinc-500">
              {args.deployment.lastDeployedAt && (
                <div>
                  Last deployed{" "}
                  {relativeTime(args.deployment.lastDeployedAt)}
                </div>
              )}
              <div>
                {args.deployment.deployCount}{" "}
                {args.deployment.deployCount === 1 ? "deploy" : "deploys"} total
              </div>
            </div>
          )}
        </div>

        {/* URL display */}
        {url && args.status === "live" && (
          <div className="flex items-center gap-2 rounded-lg border border-emerald-500/15 bg-emerald-500/5 px-3 py-2.5">
            <Globe className="size-4 shrink-0 text-emerald-400" />
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="min-w-0 flex-1 truncate font-mono text-sm text-emerald-300 transition-colors hover:text-emerald-200"
            >
              {url.replace(/^https?:\/\//, "")}
            </a>
            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                onClick={handleCopy}
                className="rounded-md p-1.5 text-emerald-400/60 transition-colors hover:bg-emerald-500/10 hover:text-emerald-300"
                title="Copy URL"
              >
                {copied ? (
                  <Check className="size-3.5" />
                ) : (
                  <Copy className="size-3.5" />
                )}
              </button>
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="rounded-md p-1.5 text-emerald-400/60 transition-colors hover:bg-emerald-500/10 hover:text-emerald-300"
                title="Open in new tab"
              >
                <ExternalLink className="size-3.5" />
              </a>
            </div>
          </div>
        )}

        {/* Error display */}
        {args.status === "failed" && args.deployment?.lastDeployError && (
          <div className="rounded-lg border border-red-500/15 bg-red-500/5 p-3">
            <button
              type="button"
              onClick={() => setErrorExpanded((prev) => !prev)}
              className="flex w-full items-center gap-2 text-left"
            >
              <AlertTriangle className="size-3.5 shrink-0 text-red-400" />
              <span className="flex-1 text-xs font-medium text-red-300">
                Deploy error
              </span>
              <ChevronDown
                className={cn(
                  "size-3 text-red-400/60 transition-transform",
                  !errorExpanded && "-rotate-90",
                )}
              />
            </button>
            {errorExpanded && (
              <pre className="mt-2 max-h-48 overflow-auto rounded-md bg-red-950/30 p-2.5 font-mono text-xs leading-relaxed text-red-200/80">
                {args.deployment.lastDeployError}
              </pre>
            )}
          </div>
        )}

        {/* Image ref (subtle) */}
        {args.deployment?.lastDeployImageRef && args.status === "live" && (
          <div className="text-xs text-zinc-600">
            Image: {args.deployment.lastDeployImageRef}
          </div>
        )}
      </div>
    </SettingsCard>
  );
}

// ---------------------------------------------------------------------------
// Environment variables section
// ---------------------------------------------------------------------------

function DeployEnvVarsSection(args: { deploymentId: string }) {
  const queryClient = useQueryClient();

  const envQuery = useQuery({
    queryKey: ["deploy-env", args.deploymentId],
    queryFn: () =>
      apiFetch<DeployEnvResponse>(
        `/api/deployments/${args.deploymentId}/env`,
      ),
  });

  const [content, setContent] = useState("");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!envQuery.data || dirty) return;
    setContent(envQuery.data.content);
  }, [dirty, envQuery.data]);

  const envQueryKey = ["deploy-env", args.deploymentId] as const;

  const saveMutation = useMutation({
    mutationFn: async (nextContent: string) => {
      await apiFetch(`/api/deployments/${args.deploymentId}/env`, {
        method: "PUT",
        body: JSON.stringify({ content: nextContent }),
      });
      return nextContent;
    },
    onSuccess: (savedContent) => {
      queryClient.setQueryData<DeployEnvResponse>(envQueryKey, {
        content: savedContent,
        hasExistingContent: savedContent.length > 0,
        keys: savedContent
          .split("\n")
          .map((line) => line.trim())
          .filter((line) => line.length > 0 && !line.startsWith("#") && line.includes("="))
          .map((line) => line.slice(0, line.indexOf("=")).trim()),
      });
      setContent(savedContent);
      setDirty(false);
      toast.success("Environment variables saved");
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to save environment variables",
      );
    },
  });

  const isEmpty = content.trim().length === 0;
  const existingKeys = envQuery.data?.keys ?? [];
  const hasHiddenExistingContent = envQuery.data?.hasExistingContent === true && content.length === 0;

  return (
    <SettingsCard>
      <div className="space-y-4">
        {/* Section header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="flex size-7 items-center justify-center rounded-md bg-zinc-800 text-zinc-400">
              <Server className="size-3.5" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-zinc-200">
                Environment Variables
              </h3>
              <p className="text-xs text-zinc-500">
                Encrypted secrets injected into the production container
              </p>
            </div>
          </div>
        </div>

        {/* Content editor */}
        {envQuery.isLoading ? (
          <div className="flex items-center justify-center py-6 text-sm text-zinc-500">
            <Loader2 className="mr-2 size-4 animate-spin" />
            Loading…
          </div>
        ) : (
          <div className="space-y-2">
            {hasHiddenExistingContent && (
              <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-xs leading-relaxed text-amber-200/80">
                Existing deploy environment variables are saved but hidden after
                load. Paste a complete replacement file below to update them.
                {existingKeys.length > 0 ? (
                  <span className="mt-1 block font-mono text-amber-100/70">
                    Saved keys: {existingKeys.join(", ")}
                  </span>
                ) : null}
              </div>
            )}
            <textarea
              value={content}
              onChange={(event) => {
                setContent(event.target.value);
                setDirty(true);
              }}
              placeholder={"API_KEY=sk-...\nSECRET_TOKEN=abc123\nREDIS_URL=redis://localhost:6379"}
              rows={isEmpty ? 4 : Math.min(Math.max(content.split("\n").length + 1, 4), 20)}
              spellCheck={false}
              className="w-full resize-y rounded-lg border border-zinc-800 bg-zinc-950/50 px-4 py-3 font-mono text-xs leading-relaxed text-zinc-200 placeholder-zinc-600 outline-none transition-colors focus:border-zinc-600"
            />
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          {dirty && (
            <span className="text-xs text-amber-400">
              Unsaved changes
            </span>
          )}
          <button
            type="button"
            onClick={() => saveMutation.mutate(content)}
            disabled={!dirty || saveMutation.isPending}
            className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-100 px-4 py-2 text-xs font-medium text-zinc-900 transition-colors hover:bg-white disabled:opacity-50"
          >
            {saveMutation.isPending ? (
              <Loader2 className="size-3 animate-spin" />
            ) : null}
            {saveMutation.isPending ? "Saving…" : "Save"}
          </button>
        </div>

        <p className="text-xs leading-relaxed text-zinc-500">
          Paste standard <span className="font-mono">KEY=VALUE</span> lines. Cord
          automatically manages <span className="font-mono">PORT</span>,{" "}
          <span className="font-mono">DATABASE_URL</span>, and{" "}
          <span className="font-mono">NODE_ENV</span> for production deploys.
        </p>

        {/* Hint */}
        <div className="flex items-start gap-2 rounded-lg bg-zinc-900/50 px-3 py-2.5 text-xs leading-relaxed text-zinc-500">
          <AlertTriangle className="mt-0.5 size-3 shrink-0 text-amber-500/60" />
          <span>
            Changes to environment variables require a new deployment to take
            effect. <code className="text-zinc-400">PORT</code> and{" "}
            <code className="text-zinc-400">DATABASE_URL</code> and{" "}
            <code className="text-zinc-400">NODE_ENV</code> are managed automatically.
          </span>
        </div>
      </div>
    </SettingsCard>
  );
}

// ---------------------------------------------------------------------------
// Deployment configuration (read-only info)
// ---------------------------------------------------------------------------

/**
 * Default memory for deployed Fly machines in MB. Must match
 * DEPLOY_DEFAULT_MEMORY_MB in fly-deploy-service.ts.
 */
const DEPLOY_INFO_DEFAULT_MEMORY_MB = 512;

/**
 * Default volume size for persistent storage in GB. Must match
 * DEPLOY_DEFAULT_VOLUME_SIZE_GB in fly-deploy-service.ts.
 */
const DEPLOY_INFO_DEFAULT_VOLUME_SIZE_GB = 1;

/**
 * Internal port the container listens on. Must match
 * DEPLOY_INTERNAL_PORT in fly-deploy-service.ts.
 */
const DEPLOY_INFO_INTERNAL_PORT = 8080;

function DeploymentConfigSection(args: { deployment: DeploymentDto }) {
  return (
    <SettingsCard>
      <div className="space-y-3">
        <div className="flex items-center gap-2.5">
          <div className="flex size-7 items-center justify-center rounded-md bg-zinc-800 text-zinc-400">
            <Cpu className="size-3.5" />
          </div>
          <h3 className="text-sm font-medium text-zinc-200">Configuration</h3>
        </div>

        <div className="divide-y divide-zinc-800/40">
          <DetailRow label="Region">
            <span className="flex items-center gap-1.5">
              <MapPin className="size-3 text-zinc-500" />
              {getRegionLabel(args.deployment.flyRegion)}
            </span>
          </DetailRow>
          <DetailRow label="Machine">
            <span className="flex items-center gap-1.5">
              <Cpu className="size-3 text-zinc-500" />
              shared-cpu-1x · {DEPLOY_INFO_DEFAULT_MEMORY_MB} MB
            </span>
          </DetailRow>
          <DetailRow label="Storage">
            <span className="flex items-center gap-1.5">
              <HardDrive className="size-3 text-zinc-500" />
              {DEPLOY_INFO_DEFAULT_VOLUME_SIZE_GB} GB persistent volume at /data
            </span>
          </DetailRow>
          <DetailRow label="Internal port">
            <span className="flex items-center gap-1.5">
              <Globe className="size-3 text-zinc-500" />
              {DEPLOY_INFO_INTERNAL_PORT}
            </span>
          </DetailRow>
          <DetailRow label="Auto-sleep">
            <span className="flex items-center gap-1.5">
              <Moon className="size-3 text-zinc-500" />
              Enabled — wakes on incoming request
            </span>
          </DetailRow>
          {args.deployment.subdomain && (
            <DetailRow label="Subdomain">
              <span className="font-mono text-xs">
                {args.deployment.subdomain}
              </span>
            </DetailRow>
          )}
          {args.deployment.flyAppName && (
            <DetailRow label="Fly app">
              <span className="font-mono text-xs">
                {args.deployment.flyAppName}
              </span>
            </DetailRow>
          )}
        </div>
      </div>
    </SettingsCard>
  );
}

// ---------------------------------------------------------------------------
// Deploy unavailable (imported / GitHub-connected repos)
// ---------------------------------------------------------------------------

function DeployUnavailableCard(args: { repoId: string }) {
  return (
    <SettingsCard className="text-center">
      <div className="py-6">
        <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-2xl border border-zinc-800 bg-zinc-900/40">
          <Rocket className="size-5 text-zinc-500" />
        </div>
        <h3 className="text-sm font-medium text-zinc-200">
          Deploy is not available for this repository
        </h3>
        <p className="mx-auto mt-2 max-w-sm text-xs leading-relaxed text-zinc-500">
          Shareable URLs are only available for repositories created with{" "}
          <span className="text-zinc-400">New project</span>. Imported and
          GitHub-connected repositories cannot use Cord deploy.
        </p>
        <Link
          to="/$repoId/settings"
          params={{ repoId: args.repoId }}
          search={{ onboarding: false }}
          className="mt-5 inline-flex items-center gap-1.5 text-xs font-medium text-zinc-400 transition-colors hover:text-zinc-200"
        >
          <ArrowLeft className="size-3" />
          Back to settings
        </Link>
      </div>
    </SettingsCard>
  );
}

// ---------------------------------------------------------------------------
// Enable deploy card (shown when deployEnabled is false)
// ---------------------------------------------------------------------------

function EnableDeployCard(args: {
  enabling: boolean;
  onEnable: () => void;
}) {
  return (
    <SettingsCard className="text-center">
      <div className="py-6">
        <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-2xl border border-emerald-500/15 bg-emerald-500/5">
          <Rocket className="size-5 text-emerald-400/70" />
        </div>
        <h3 className="text-sm font-medium text-zinc-200">
          Get a shareable URL
        </h3>
        <p className="mx-auto mt-2 max-w-sm text-xs leading-relaxed text-zinc-500">
          Enable deployment to get a public URL for your app that runs 24/7.
          New threads will be guided toward a deployable architecture
          (Dockerfile, environment variables, etc.).
        </p>
        <button
          type="button"
          onClick={args.onEnable}
          disabled={args.enabling}
          className="mt-5 inline-flex items-center gap-2 rounded-xl bg-emerald-400 px-5 py-2.5 text-sm font-semibold text-emerald-950 transition-colors hover:bg-emerald-300 disabled:opacity-50"
        >
          {args.enabling ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Rocket className="size-4" />
          )}
          {args.enabling ? "Enabling…" : "Enable deploy"}
        </button>
      </div>
    </SettingsCard>
  );
}

// ---------------------------------------------------------------------------
// Danger zone
// ---------------------------------------------------------------------------

function DangerZoneSection(args: {
  deployment: DeploymentDto | null;
  status: DeploymentStatus;
  onDestroy: () => void;
  destroying: boolean;
  onDisableDeploy: () => void;
  disabling: boolean;
}) {
  const [destroyConfirmOpen, setDestroyConfirmOpen] = useState(false);
  const [disableConfirmOpen, setDisableConfirmOpen] = useState(false);
  const showDestroy =
    args.deployment &&
    args.status !== "not_deployed" &&
    args.status !== "destroyed";

  return (
    <div className="space-y-3">
      {/* Destroy deployment */}
      {showDestroy && (
        <div className="rounded-xl border border-red-500/10 bg-red-500/[0.02] p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-sm font-medium text-red-300/90">
                Destroy deployment
              </h3>
              <p className="mt-1 text-xs leading-relaxed text-zinc-500">
                Permanently destroy this deployment, including the Fly.io app,
                machine, and storage volume. This cannot be undone.
              </p>
            </div>
            {!destroyConfirmOpen ? (
              <button
                type="button"
                onClick={() => setDestroyConfirmOpen(true)}
                className="shrink-0 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-1.5 text-xs font-medium text-red-300 transition-colors hover:bg-red-500/20"
              >
                Destroy
              </button>
            ) : (
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  onClick={() => setDestroyConfirmOpen(false)}
                  className="rounded-lg border border-zinc-700 px-3 py-1.5 text-xs font-medium text-zinc-400 transition-colors hover:bg-zinc-800"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDestroyConfirmOpen(false);
                    args.onDestroy();
                  }}
                  disabled={args.destroying}
                  className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-red-500 disabled:opacity-50"
                >
                  {args.destroying ? (
                    <Loader2 className="size-3 animate-spin" />
                  ) : (
                    "Confirm destroy"
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Disable deploy */}
      <div className="rounded-xl border border-zinc-800/40 bg-zinc-900/20 p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-sm font-medium text-zinc-300">
              Disable deploy
            </h3>
            <p className="mt-1 text-xs leading-relaxed text-zinc-500">
              Remove deployment guidance from the AI. Your existing deployment
              (if any) stays live until you destroy it.
            </p>
          </div>
          {!disableConfirmOpen ? (
            <button
              type="button"
              onClick={() => setDisableConfirmOpen(true)}
              className="shrink-0 rounded-lg border border-zinc-700 px-3 py-1.5 text-xs font-medium text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-200"
            >
              Disable
            </button>
          ) : (
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={() => setDisableConfirmOpen(false)}
                className="rounded-lg border border-zinc-700 px-3 py-1.5 text-xs font-medium text-zinc-400 transition-colors hover:bg-zinc-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setDisableConfirmOpen(false);
                  args.onDisableDeploy();
                }}
                disabled={args.disabling}
                className="rounded-lg border border-zinc-600 bg-zinc-800 px-3 py-1.5 text-xs font-medium text-zinc-200 transition-colors hover:bg-zinc-700 disabled:opacity-50"
              >
                {args.disabling ? (
                  <Loader2 className="size-3 animate-spin" />
                ) : (
                  "Confirm disable"
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
